import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { multiFactor } from "firebase/auth";
import { auth } from "@/firebase/firebase-config";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { SMSMFASetup } from "@/components/SMSMFASetup";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

export default function RequireAdmin({ children }: { children: JSX.Element }) {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [has2FA, setHas2FA] = useState(false);
  const [loading2FA, setLoading2FA] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    const check2FA = async () => {
      try {
        const authInstance = auth();
        const user = authInstance.currentUser;
        
        if (user && isAdmin) {
          const enrolledFactors = multiFactor(user).enrolledFactors;
          const has2FAEnabled = enrolledFactors.length > 0;
          setHas2FA(has2FAEnabled);
          setShowSetup(!has2FAEnabled);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error("Error checking 2FA status:", err);
        }
      } finally {
        setLoading2FA(false);
      }
    };

    if (!adminLoading && isAdmin) {
      check2FA();
    } else if (!adminLoading) {
      setLoading2FA(false);
    }
  }, [isAdmin, adminLoading]);

  if (adminLoading || (isAdmin && loading2FA)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Require 2FA for admins
  if (!has2FA && showSetup) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-slate-50 dark:bg-slate-900">
        <Alert variant="default" className="mb-6 max-w-2xl">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Two-Factor Authentication Required</AlertTitle>
          <AlertDescription>
            For security reasons, all admin accounts must have two-factor authentication enabled.
            Please add your phone number to receive verification codes when signing in.
          </AlertDescription>
        </Alert>
        <SMSMFASetup
          user={auth().currentUser}
          onComplete={() => {
            setHas2FA(true);
            setShowSetup(false);
          }}
        />
      </div>
    );
  }

  return children;
}
